class BackInStockFunctions {
  /**
   * @note It might be worth abstracting this further, for example simplifying the function signature
   * to have object containing email and phone number. That way this API could be easier to swap out
   * for other back ends in the future. It doesn't seem necessary at the moment though. - DW
   *
   * @param {string | null} email
   * @param {number} variantId
   * @param {number} productId
   * @param {BackInStockAttributes} [attributes]
   */
  static async subscribe (email, variantId, productId, attributes) {
    /** @type {BackInStockSuccessResponse | BackInStockErrorResponse} */

    const bodyData = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: `${email}`,
              },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    }

    const options = {
      method: 'POST',
      headers: {
        revision: window.theme.klaviyo.apiVersion,
        'content-type': 'application/json',
      },
      body: JSON.stringify(bodyData),
    }

    const response = await fetch(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${window.theme.klaviyo.apiKey}`,
      options,
    )

    if (!response.ok) {
      const data = await response.json()
      throw data.errors
    }

    return response
  }

  /**
   * @param {string} email
   */
  static async newsletterSubscribe (emailAddress) {
    const bodyData = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: emailAddress,
                properties: { 'Accepts Marketing': 'true' },
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: window.theme.klaviyo.newsletterListId,
            },
          },
        },
      },
    }

    const options = {
      method: 'POST',
      headers: {
        revision: window.theme.klaviyo.apiVersion,
        'content-type': 'application/json',
      },
      body: JSON.stringify(bodyData),
    }

    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${window.theme.klaviyo.apiKey}`,
      options,
    )

    if (!response.ok) {
      const data = await response.json()
      throw data.errors
    }

    return response
  }
}

export class BackInStock extends window.BAO.CustomElement() {
  connectedCallback () {
    this.setupListeners()
  }

  setupListeners () {
    super.setupListeners()

    if (!this.hasEl('form')) return
    this.listeners.add(
      this.getEl('form'),
      'submit',
      this.handleSubmit.bind(this),
    )

    if (this?.externalEls?.['mobile-trigger']?.exists) {
      this.externalEls['mobile-trigger'].elements.forEach(mobileTriggerEl => {
        this.listeners.add(
          mobileTriggerEl,
          'click',
          this.changeVariant.bind(this),
        )
      })
    }

    if (this?.els?.['desktop-trigger']?.exists) {
      this.els['desktop-trigger'].elements.forEach(desktopTriggerEl => {
        this.listeners.add(
          desktopTriggerEl,
          'click',
          this.showDesktopForm.bind(this),
        )
      })
    }
  }

  showDesktopForm () {
    this.classList.add('bis-BackInStock-formVisible')
  }

  changeVariant (e) {
    this.setAttribute('data-variant-id', e.currentTarget.dataset.variantId)
  }

  async handleSubmit (event) {
    event.preventDefault()
    if (this.email.length < 1) return
    this.sendBIS()
    this.newsletterSubscribe()
    // this.handleResponse(BISResponse)
    return false
  }

  async sendBIS () {
    const response = await BackInStockFunctions.subscribe(
      this.email,
      this.dataset.variantId,
      this.dataset.productId,
    )

    this.log('subscribe', { response })

    this.handleResponse(response)
  }

  async newsletterSubscribe () {
    const acceptsMarketing = !!(
      this?.els?.marketing?.exists &&
      this?.els?.marketing?.element?.checked === true
    )
    if (acceptsMarketing) {
      const response = await BackInStockFunctions.newsletterSubscribe(
        this.email,
      )
      this.log('newsletterSubscribe', { response })

      this.handleResponse(response)

      return response.ok
    }

    return false
  }

  handleResponse (response) {
    const responseEl = this.getEl('response')
    if (!responseEl) return
    this.toggleResponse(false)

    responseEl.innerHTML = ''
    responseEl.dataset.status = response.status
    const message = response.ok
      ? responseEl.dataset.success
      : responseEl.dataset.error
    responseEl.innerHTML = message ? `<p>${message}</p>` : responseEl.innerHTML

    this.toggleResponse(true)

    window.setTimeout(() => {
      responseEl.innerHTML = ''

      if (responseEl.dataset.status === 'ok') {
        if (this?.els?.email?.exists) {
          this.els.email.element.value = ''
        }

        if (this?.els?.marketing?.exists) {
          this.els.marketing.element.checked = false
        }

        this.classList.remove('bis-BackInStock-formVisible')

        if (document.querySelector('.bis-Drawer_Close')) {
          document.querySelector('.bis-Drawer_Close').click()
        }
      }
    }, 3000)
  }

  toggleResponse (isVisible) {
    this.getEl('response').setAttribute('aria-hidden', (!isVisible).toString())
  }

  hasEl (elementName) {
    return this.els[elementName] && this.els[elementName].exists
  }

  getEl (elementName) {
    return this.hasEl(elementName) ? this.els[elementName].element : null
  }

  getExternalEl (elementName) {
    return this.hasExternalEl(elementName)
      ? this.externalEls[elementName].element
      : null
  }

  get email () {
    const emailEl = this.getEl('email')
    return emailEl ? emailEl.value : ''
  }

  get productId () {
    return this._productId
  }

  set productId (productId) {
    this._productId = productId
  }

  get variantId () {
    return this._variantId
  }

  set variantId (variantId) {
    this._variantId = variantId
  }
}

if (!customElements.get('back-in-stock')) {
  customElements.define('back-in-stock', BackInStock)
}
